import type { ApiResponse } from '@ovation/core'
import { useAuthStore } from './auth-store'

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')

/**
 * Typed error mirroring the API's `{ error: { code, message, details } }` envelope.
 * See `.claude/rules/api-conventions.md`.
 */
export class ApiClientError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: unknown

  constructor(params: {
    code: string
    message: string
    status: number
    details?: unknown
  }) {
    super(params.message)
    this.name = 'ApiClientError'
    this.code = params.code
    this.status = params.status
    this.details = params.details
  }
}

interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Skip auth header injection (used for /auth/login, /auth/refresh). */
  skipAuth?: boolean
}

/**
 * Thin fetch wrapper that injects the JWT, parses the standard response envelope,
 * and transparently refreshes the access token on 401 once before failing.
 */
export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const response = await rawFetch(path, options)

  if (response.status === 401 && !options.skipAuth) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      const retry = await rawFetch(path, options)
      return parseResponse<T>(retry)
    }
    useAuthStore.getState().logout()
  }

  return parseResponse<T>(response)
}

async function rawFetch(path: string, options: ApiFetchOptions): Promise<Response> {
  const { body, skipAuth, headers, ...rest } = options
  const accessToken = useAuthStore.getState().accessToken

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(headers as Record<string, string> | undefined),
  }
  if (!skipAuth && accessToken) {
    finalHeaders.Authorization = `Bearer ${accessToken}`
  }

  const init: RequestInit = {
    ...rest,
    headers: finalHeaders,
  }
  if (body !== undefined) {
    init.body = JSON.stringify(body)
  }
  return fetch(`${API_BASE_URL}${path}`, init)
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T
  }

  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    throw new ApiClientError({
      code: 'invalid_response',
      message: 'Server returned a non-JSON response',
      status: response.status,
    })
  }

  if (!response.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: unknown } })
      .error
    throw new ApiClientError({
      code: err?.code ?? 'unknown_error',
      message: err?.message ?? `Request failed with status ${response.status}`,
      status: response.status,
      details: err?.details,
    })
  }

  return (payload as ApiResponse<T>).data
}

interface RefreshResponse {
  accessToken: string
}

async function tryRefresh(): Promise<boolean> {
  const refreshToken = useAuthStore.getState().refreshToken
  if (!refreshToken) return false

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
    if (!response.ok) return false
    const payload = (await response.json()) as ApiResponse<RefreshResponse>
    useAuthStore.getState().setAccessToken(payload.data.accessToken)
    return true
  } catch {
    return false
  }
}
